import type { Composer } from 'vue-i18n';
import type { Router } from 'vue-router';
import type { QuasarLanguage } from 'quasar';
import { Quasar } from 'quasar';
import type { AppLocale } from './index';
import { isSupportedLocale, loadLocaleMessages } from './index';
import { loadFeatureI18n } from './load-feature';
import { loadQuasarLang } from './locale-packs';
import { refreshDocumentTitle } from 'src/router/document-title';

// Sổ sách TƯỜNG MINH thay vì suy từ hình dạng cây message. Trước đây dùng
// `i18n.availableLocales.includes(locale)`, nhưng bất kỳ `mergeLocaleMessage` nào vào một locale
// chưa nạp global (một partial prefetch chẳng hạn) cũng tạo khoá trong cây ⇒ lần sau
// `availableLocales` báo "có rồi" ⇒ BỎ QUA nạp global ⇒ cả app raw key.
// `load-feature.ts` đã có sẵn comment cảnh báo đúng cái bẫy này.
const loadedGlobals = new Set<AppLocale>();

/** `boot/i18n.ts` gọi để khai locale đã nạp sẵn lúc khởi tạo. */
export const markGlobalLoaded = (locale: AppLocale): void => {
  loadedGlobals.add(locale);
};

/**
 * Đổi ngôn ngữ. Hàm DUY NHẤT được phép ghi `i18n.locale`.
 *
 * Bất biến lấy từ example chính thức của intlify (`examples/lazy-loading/vite/src/router.ts`):
 * **nạp xong MỚI lật locale**.
 *
 *     if (!i18n.global.availableLocales.includes(paramsLocale)) await loadLocaleMessages(...)
 *     setI18nLanguage(i18n, paramsLocale)     // lật locale SAU
 *
 * Làm ngược thứ tự này là đẻ ra hai lớp lỗi cùng lúc:
 *   1. Bundle co-locate của màn đang mở chưa tồn tại ở locale mới ⇒ màn giữ nguyên ngôn ngữ cũ
 *      (rơi về `fallbackLocale`), hoặc ra nguyên tên khoá nếu cây fallback cũng chưa có khoá đó.
 *   2. Không cách nào huỷ được: `mergeLocaleMessage` KHÔNG tạo dependency cho một `t()` đã trượt
 *      khoá (đã đo trên vue-i18n 11.4.2), nên nạp muộn không repaint — chuỗi kẹt sai vĩnh viễn.
 *
 * Partial KHÔNG nằm trong đây. Chúng tự nghe `locale` và tự nạp bundle của mình
 * (`usePartialI18n`), nên hàm này không cần biết partial nào đang mount.
 *
 * @returns `false` khi locale không hợp lệ, khi bundle GLOBAL hỏng, hoặc khi gói ngôn ngữ Quasar
 *   hỏng — và khi đó **locale không bị đổi**, đúng chủ ý sẵn có của `useLang.ensureLocaleLoaded`:
 *   thà giữ ngôn ngữ cũ còn hơn đổi nửa vời.
 *
 *   ⚠ **KHÔNG** trả `false` khi bundle CO-LOCATE của màn hỏng. `loadFeatureI18n` nuốt lỗi
 *   (`load-feature.ts` — `mergeMessages` chỉ `console.error`), nên `try` dưới đây không bao giờ
 *   bắt được nhánh đó. Đây là chủ ý chứ không phải sót: bundle màn đã có lane `en` làm lưới, và
 *   chặn cả lần đổi ngôn ngữ chỉ vì MỘT màn hỏng chunk là phạt quá tay. Bản trước JSDoc ghi
 *   "nạp lỗi ⇒ false" nói quá — chunk co-locate 404 vẫn trả `true`, vẫn lật locale, vẫn ghi cookie.
 */
// Bộ tuần tự. Example của intlify không cần vì locale nằm trong URL nên vue-router xếp hàng
// giúp; ở đây đổi ngôn ngữ KHÔNG sinh navigation, nên hai lần bấm liền tay sẽ chạy song song và
// lần nào `await` xong SAU sẽ thắng — tức lần bấm ĐẦU ghi đè lần bấm SAU.
let seq = 0;

export async function setI18nLanguage(
  i18n: Composer,
  router: Router,
  locale: string,
): Promise<boolean> {
  if (!isSupportedLocale(locale)) {
    return false;
  }

  const my = ++seq;
  let quasarLang: QuasarLanguage;

  try {
    // ① Bundle global của locale đích.
    if (!loadedGlobals.has(locale)) {
      i18n.setLocaleMessage(locale, await loadLocaleMessages(locale));
      loadedGlobals.add(locale);
    }
    // ② Bundle co-locate của route đang đứng — truyền locale TƯỜNG MINH vì `locale` chưa lật.
    await loadFeatureI18n(i18n, router.currentRoute.value, locale);
    // ③ Gói ngôn ngữ Quasar. TRONG `try` và TRƯỚC cổng `seq` — mọi `await` phải nằm trước cổng,
    // không thì cổng vô nghĩa (kết quả về trễ vẫn ghi đè) và lỗi chunk thành ném thay vì
    // `return false`, để lại trạng thái nửa vời qua promise không ai await ở `App.vue`.
    quasarLang = await loadQuasarLang(locale);
  } catch (error) {
    console.error(`[i18n] Không đổi được sang "${locale}"`, error);
    return false;
  }

  if (my !== seq) {
    return false; // đã có lần đổi mới hơn: bỏ kết quả này, đừng ghi đè locale/cookie
  }

  // ④ Lật MỘT lần, sau khi mọi message cần cho khung hình kế tiếp đã có mặt. Từ đây trở xuống
  // KHÔNG còn `await` nào — cả khối là đồng bộ nên không cửa nào cho một lần đổi khác chen vào.
  // Không `nextTick`: đã đo, không ai trong luồng này đọc DOM sau đây, và render đã được
  // schedule sẵn — Vue tự flush. Gói Quasar lật CÙNG LÚC, không thì app tiếng Việt kèm bảng
  // "No data available".
  i18n.locale.value = locale;
  // `Quasar.lang.set` tự đặt `<html lang>` và `dir` (`quasar/src/plugins/lang/Lang.js`), và đặt
  // bằng `isoName` — `en-US`/`zh-CN`, đủ vùng. Bản trước có thêm một dòng
  // `documentElement.setAttribute('lang', locale)` ngay dưới đây: nó CHẠY SAU nên đè mất isoName
  // bằng mã cụt (`zh`), tức hai chủ cùng ghi một thuộc tính và bản kém chính xác thắng. Đã bỏ.
  Quasar.lang.set(quasarLang);
  // Tiêu đề tab cũng là copy: `installDocumentTitle` chỉ chạy khi ĐIỀU HƯỚNG, nên đứng yên một
  // chỗ mà đổi ngôn ngữ thì tab giữ nguyên chữ cũ. Hàm này tự guard `document`.
  //
  // Bọc `try`: `i18n.t()` bên trong có thể ném (message ICU hỏng ở `titleKey`), mà `App.vue` gọi
  // `initialLocale()` không `await` không `.catch()` ⇒ ném ở đây là unhandled rejection im lặng
  // lúc khởi động, sau khi locale ĐÃ lật. Tiêu đề sai không đáng đánh đổi lấy chuyện đó.
  try {
    refreshDocumentTitle(i18n, router);
  } catch (error) {
    console.error('[i18n] Không đặt được tiêu đề tab', error);
  }
  return true;
}
